import React, { useContext, useState } from 'react';
import { useMediaPredicate } from 'react-media-hook';

import { media } from 'utilities/browser';
import { addToCart, redirectToCart } from 'features/cart';
import { Reporter } from 'features/dataCapture';
import { setGenericEvent } from 'features/tealium/ga4/custom';
import { dataCaptureTimeout } from 'ui/config';
import { Button } from 'components/Button';
import { trackAddToCartDy } from 'features/tealium/dynamicYield';

import { FrequentlyBoughtTogetherContext } from '../context';
import dynamicImport from '../../../features/webpack/dynamicImport.macro';

import './FrequentlyBoughtTogetherAddItemsToCart.scss';

type FrequentlyBoughtTogetherAddToCartButtonProps = {
	dataCaptureMainElement: string,
	skipMainProductSkuView?: boolean
}
// eslint-disable-next-line max-len
function FrequentlyBoughtTogetherAddItemsToCart ({ children, dataCaptureMainElement, skipMainProductSkuView = false }: React.PropsWithChildren<FrequentlyBoughtTogetherAddToCartButtonProps>) {

	const { selectedProducts, mainShortSku } = useContext(FrequentlyBoughtTogetherContext);
	const [ loading, setLoading ] = useState(false);
	const isMediaDesktop = useMediaPredicate(media.DESKTOP);
	const [ primaryCategory ] = window.utag_data.product_category;

	// data capture
	const { reporter } = window.lp.dataCapture;
	const dataCapturePromises: Array<Promise<any>> = [];
	const parentElement = document.getElementById(dataCaptureMainElement);

	let targetBtn: HTMLButtonElement | null = null;
	let addToCartRecordId: any;

	const waitForDataCaptureAndRedirectToCart = () => {

		setGenericEvent({
			event_name: 'pdp_fbt_addtocart',
			pdp_item_id: mainShortSku,
			pdp_prime_cat: primaryCategory,
			fbt_item_info: selectedProducts.map((sp) => sp.ShortSku).join()
		});

		if (!window.lp.globals.enableDataCapture) {

			redirectToCart();
			return;

		}

		selectedProducts.forEach((product) => {

			const itemImg = parentElement?.querySelector(
				`img[data-sku="${product.ShortSku}"]`
			);

			if ((!skipMainProductSkuView || (skipMainProductSkuView && !product.IsMainProduct))) {

				const skuViewRecord = reporter.getRecordByElement(itemImg);
				const skuViewRequest = skuViewRecord
					? skuViewRecord.promise
					: reporter.track(Reporter.getSkuViewData(itemImg));
				dataCapturePromises.push(skuViewRequest);

			}

			// DY - Add To Cart event
			if (window.top.DY) {

				try {

					const dyPrice: number = typeof product.DisplayPrice === 'string'
						? parseFloat(product.DisplayPrice)
						: product.DisplayPrice;
					trackAddToCartDy(product.ShortSku, dyPrice, 1);

				} catch (e) {

					// eslint-disable-next-line no-console
					console.warn('DY API is not available', e);

				}

			}

		});

		const atcRecord = reporter.getRecordById(addToCartRecordId);
		const atcRequest = atcRecord.promise;
		dataCapturePromises.push(atcRequest);
		const timeout = window.setTimeout(redirectToCart, dataCaptureTimeout);

		Promise.all(dataCapturePromises).finally(() => {

			window.clearTimeout(timeout);
			redirectToCart();

		});

	};

	const addToCartClickFun = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {

		targetBtn = e.target as HTMLButtonElement;
		e.preventDefault();

		const data = selectedProducts.map((item) => ({
			ShortSku: item.ShortSku,
			Quantity: 1,
			SkuInputType: item.SkuInputType,
			IsOpenBox: item.IsOpenBox
		}));

		if (window.lp.globals.enableDataCapture) {

			reporter.trackAddToCart(data, targetBtn);
			addToCartRecordId = reporter.getLastRecordId();

		}

		setLoading(true);

		addToCart({
			arrData: data,
			successCallback: waitForDataCaptureAndRedirectToCart,
			errorCallback: (errors, xhr, response) => {

				dynamicImport(
					'mobile/showAddToCartFailureModal',
					'../../../features/cart/mobile/showAddToCartFailureModal'
				).then((module: any) => {

					const showAddToCartFailureModal = module.default;
					showAddToCartFailureModal({
						cartStatus: errors,
						response,
						fromModal: false
					});
					setLoading(false);

				});

			}
		});

	};

	return (
		// @ts-ignore
		// TODO: _client\components\Button\Button.js needs to be converted to tsx
		<Button
			className="FrequentlyBoughtTogetherAddItemsToCart"
			buttonStyleVariant="secondary"
			type="button"
			loading={loading}
			aria-disabled={loading}
			onClick={addToCartClickFun}
			size={isMediaDesktop ? 'large' : ''}
		>
			{children}
		</Button>
	);

}

export default FrequentlyBoughtTogetherAddItemsToCart;
